import type { Ergebnis, JahresStatistik } from '@baba/engine/types';
import { euro, zahl } from '../format.ts';

interface Props {
  ergebnis: Ergebnis;
}

interface Reihe {
  titel: string;
  farbe: string;
  wert: (s: JahresStatistik) => number;
}

/** Gestapeltes Balkendiagramm, ein Balken je Kalenderjahr. */
function Balkendiagramm({
  jahre,
  reihen,
}: {
  jahre: JahresStatistik[];
  reihen: Reihe[];
}) {
  const summen = jahre.map((j) =>
    reihen.reduce((s, r) => s + r.wert(j), 0),
  );
  const groesste = Math.max(...summen, 1);

  return (
    <>
      <div className="balken" role="img" aria-hidden="true">
        {jahre.map((j, i) => (
          <div key={j.jahr} className="balken__saeule">
            {reihen.map((r) => {
              const anteil = (r.wert(j) / groesste) * 100;
              return anteil > 0 ? (
                <div
                  key={r.titel}
                  className="balken__stueck"
                  style={{ height: `${anteil}%`, background: r.farbe }}
                />
              ) : null;
            })}
            {summen[i] === 0 && (
              <div
                className="balken__stueck"
                style={{ height: '2px', background: 'var(--linie)' }}
              />
            )}
          </div>
        ))}
      </div>
      <div className="balken__achse">
        {jahre.map((j) => (
          <span key={j.jahr}>{String(j.jahr).slice(2)}</span>
        ))}
      </div>
      {reihen.length > 1 && (
        <div className="legende">
          {reihen.map((r) => (
            <span key={r.titel}>
              <i style={{ background: r.farbe }} />
              {r.titel}
            </span>
          ))}
        </div>
      )}
    </>
  );
}

function ProKm({ wert }: { wert: number | null }) {
  return (
    <span className="wert">
      {wert == null ? '–' : `${zahl(wert, 4)} €`}
    </span>
  );
}

export function Statistik({ ergebnis }: Props) {
  const jahre = ergebnis.statistik;
  const laufend = jahre[jahre.length - 1];

  if (!laufend) return <p className="leer">Noch keine Daten.</p>;

  const mehrjaehrig = jahre.length > 1;

  return (
    <>
      <section className="tafel">
        <p className="etikett">Laufendes Jahr {laufend.jahr}</p>

        <div className="zeile">
          <span>Gefahrene Kilometer</span>
          <span className="wert">{zahl(laufend.kmJahr, 0)} km</span>
        </div>
        <div className="zeile">
          <span>Gesamtkosten Treibstoff</span>
          <span className="wert">{euro(laufend.kostenTreibstoff)}</span>
        </div>
        <div className="zeile">
          <span>Gesamtkosten Instandhaltung</span>
          <span className="wert">{euro(laufend.kostenReparatur)}</span>
        </div>
        {laufend.kostenFix > 0 && (
          <div className="zeile">
            <span>Fixkosten</span>
            <span className="wert">{euro(laufend.kostenFix)}</span>
          </div>
        )}
        <div className="zeile">
          <span>Kosten pro km, Treibstoff</span>
          <ProKm wert={laufend.proKmTreibstoff} />
        </div>
        <div className="zeile">
          <span>Kosten pro km, Instandhaltung</span>
          <ProKm wert={laufend.proKmReparatur} />
        </div>
        <div className="zeile">
          <span>Kosten pro km, zusammen</span>
          <ProKm wert={laufend.proKmTreibstoffUndReparatur} />
        </div>
        {laufend.kostenFix > 0 && (
          <>
            <div className="zeile">
              <span>Fixkosten pro km</span>
              <ProKm wert={laufend.proKmFix} />
            </div>
            <div className="zeile">
              <span>Kosten pro km inkl. Fixkosten</span>
              <ProKm wert={laufend.proKmGesamt} />
            </div>
          </>
        )}
        <div className="zeile">
          <span>km mit MitfahrerInnen</span>
          <span className="wert">{zahl(laufend.kmMitMitfahrern, 0)} km</span>
        </div>
        <div className="zeile">
          <span>km ohne MitfahrerInnen</span>
          <span className="wert">{zahl(laufend.kmOhneMitfahrer, 0)} km</span>
        </div>
        <div className="zeile">
          <span>km nicht als Ausflug erfasst</span>
          <span className="wert">
            {zahl(laufend.kmNichtAlsAusflugErfasst, 0)} km
          </span>
        </div>
      </section>

      {mehrjaehrig ? (
        <>
          <section className="tafel">
            <p className="etikett">Gesamtkosten je Jahr</p>
            <Balkendiagramm
              jahre={jahre}
              reihen={[
                {
                  titel: 'Treibstoff',
                  farbe: 'var(--tanken)',
                  wert: (s) => s.kostenTreibstoff,
                },
                {
                  titel: 'Instandhaltung',
                  farbe: 'var(--reparatur)',
                  wert: (s) => s.kostenReparatur,
                },
                {
                  titel: 'Fixkosten',
                  farbe: 'var(--fixkosten)',
                  wert: (s) => s.kostenFix,
                },
              ]}
            />
          </section>

          <section className="tafel">
            <p className="etikett">Gefahrene Kilometer je Jahr</p>
            <Balkendiagramm
              jahre={jahre}
              reihen={[
                {
                  titel: 'Kilometer',
                  farbe: 'var(--ausflug)',
                  wert: (s) => s.kmJahr,
                },
              ]}
            />
          </section>

          <section className="tafel">
            <p className="etikett">Kosten pro Kilometer je Jahr</p>
            <Balkendiagramm
              jahre={jahre}
              reihen={[
                {
                  titel: 'Kosten pro km',
                  farbe: 'var(--tinte)',
                  wert: (s) => s.proKmGesamt ?? 0,
                },
              ]}
            />
          </section>
        </>
      ) : (
        <p className="leer">
          Die Diagramme vergleichen Kalenderjahre. Sie erscheinen, sobald das
          zweite Jahr angefangen hat.
        </p>
      )}
    </>
  );
}
